import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
// import './Checkout.css';
import { PayPalButtons, usePayPalScriptReducer } from "@paypal/react-paypal-js";
import { useAuth } from '../context/AuthContext';
import { API_ENDPOINTS } from '../config/apiConfig';
import { 
  CURRENCY_CONFIG, 
  convertCurrency, 
  formatPrice, 
  getCurrencyDisplay 
} from '../config/currencyConfig';

const Checkout = () => {
    const navigate = useNavigate();
    const { user, token, updateUser } = useAuth();
    const [{ options, isPending }, dispatch] = usePayPalScriptReducer();
    const [currency, setCurrency] = useState(options.currency || CURRENCY_CONFIG.DEFAULT_CURRENCY);
    const [message, setMessage] = useState("");

    const onCurrencyChange = ({ target: { value } }) => {
        setCurrency(value);
        dispatch({
            type: "resetOptions",
            value: {
                ...options,
                currency: value,
            },
        });
    }

    const onCreateOrder = (data, actions) => {
        const amount = convertCurrency(CURRENCY_CONFIG.DEFAULT_AMOUNT, currency);
        
        return actions.order.create({
            purchase_units: [
                {
                    amount: {
                        value: amount.toString(),
                        currency_code: currency,
                    },
                    description: "Premium Subscription - Alberta Class 7 Driving Test",
                },
            ],
        });
    }

    const onApproveOrder = (data, actions) => {
        return actions.order.capture().then((details) => {
            const name = details.payer.name.given_name;
            const amount = convertCurrency(CURRENCY_CONFIG.DEFAULT_AMOUNT, currency);
            
            // If user is already logged in, upgrade their subscription
            if (user && token) {
                setMessage(`Thank you ${name}! Your payment was successful. Upgrading your account...`);
                
                // Update user subscription to premium
                fetch(API_ENDPOINTS.SUBSCRIPTION, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ subscription: 'premium' })
                })
                .then(res => res.json())
                .then(data => {
                    if (data.user) {
                        updateUser(data.user);
                    }
                    // Redirect to dashboard after 2 seconds
                    setTimeout(() => {
                        navigate('/dashboard');
                    }, 2000);
                })
                .catch(err => {
                    console.error('Failed to update subscription:', err);
                    setMessage('Payment successful! Please refresh the page.');
                });
            } else {
                // New user - store premium purchase flag and redirect to register
                setMessage(`Thank you ${name}! Your payment was successful. Redirecting to create your Premium account...`);
                
                localStorage.setItem('premiumPurchase', 'true');
                localStorage.setItem('purchaseDate', new Date().toISOString());
                localStorage.setItem('purchaseDetails', JSON.stringify({
                    orderId: data.orderID,
                    payerName: name,
                    amount: amount.toString(),
                    currency: currency
                }));
                
                // Redirect to register page with premium flag after 2 seconds
                setTimeout(() => {
                    navigate('/register?premium=true');
                }, 2000);
            }
        });
    }

    const onError = (err) => {
        setMessage("An error occurred with your payment. Please try again.");
        console.error("PayPal Error:", err);
    }

    return (
        <div className="checkout" style={styles.container}>
            <div style={styles.card}>
                <h1 style={styles.title}>Upgrade to Premium</h1>
                <div style={styles.benefits}>
                    <h3 style={styles.benefitsTitle}>What you'll get:</h3>
                    <ul style={styles.benefitsList}>
                        <li>✓ 500+ practice questions</li>
                        <li>✓ John AI Assistant for instant help</li>
                        <li>✓ Detailed explanations for all answers</li>
                        <li>✓ Unlimited access to all mock tests</li>
                        <li>✓ Priority support</li>
                        <li>✓ All future updates included</li>
                    </ul>
                </div>

                <div style={styles.price}>
                    <h2 style={styles.priceAmount}>
                        {formatPrice(convertCurrency(CURRENCY_CONFIG.DEFAULT_AMOUNT, currency), currency, false)}
                    </h2>
                    <p style={styles.priceText}>One-time payment • Lifetime access</p>
                    <p style={styles.currencyInfo}>(Default: {formatPrice(CURRENCY_CONFIG.DEFAULT_AMOUNT, CURRENCY_CONFIG.DEFAULT_CURRENCY, false)})</p>
                </div>

                {message && (
                    <div style={message.includes('error') ? styles.errorMessage : styles.successMessage}>
                        {message}
                    </div>
                )}

                {isPending ? (
                    <div style={styles.loading}>
                        <p>Loading PayPal...</p>
                    </div>
                ) : (
                    <>
                        <div style={styles.currencySelector}>
                            <label style={styles.label}>Select Currency:</label>
                            <select 
                                value={currency} 
                                onChange={onCurrencyChange}
                                style={styles.select}
                            >
                                {CURRENCY_CONFIG.CURRENCIES.map(curr => (
                                    <option key={curr.code} value={curr.code}>
                                        {curr.emoji} {curr.code} - {curr.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div style={styles.paypalButtons}>
                            <PayPalButtons 
                                style={{ 
                                    layout: "vertical",
                                    color: "gold",
                                    shape: "rect",
                                    label: "paypal"
                                }}
                                createOrder={(data, actions) => onCreateOrder(data, actions)}
                                onApprove={(data, actions) => onApproveOrder(data, actions)}
                                onError={onError}
                            />
                        </div>
                    </>
                )}

                <button 
                    onClick={() => navigate('/')} 
                    style={styles.backButton}
                >
                    ← Back to Home
                </button>
            </div>
        </div>
    );
}

const styles = {
    container: {
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '20px',
    },
    card: {
        background: 'white',
        borderRadius: '16px',
        padding: '40px',
        maxWidth: '600px',
        width: '100%',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    },
    title: {
        fontSize: '32px',
        fontWeight: 'bold',
        color: '#333',
        textAlign: 'center',
        marginBottom: '30px',
    },
    benefits: {
        background: '#f8f9fa',
        padding: '20px',
        borderRadius: '8px',
        marginBottom: '30px',
    },
    benefitsTitle: {
        fontSize: '18px',
        fontWeight: '600',
        color: '#333',
        marginBottom: '15px',
    },
    benefitsList: {
        listStyle: 'none',
        padding: 0,
        margin: 0,
    },
    price: {
        textAlign: 'center',
        marginBottom: '30px',
    },
    priceAmount: {
        fontSize: '42px',
        fontWeight: 'bold',
        color: '#667eea',
        margin: '10px 0',
    },
    priceText: {
        color: '#666',
        fontSize: '14px',
    },
    currencyInfo: {
        color: '#999',
        fontSize: '12px',
        marginTop: '8px',
        fontStyle: 'italic',
    },
    currencySelector: {
        marginBottom: '20px',
    },
    label: {
        display: 'block',
        marginBottom: '8px',
        fontWeight: '600',
        color: '#333',
    },
    select: {
        width: '100%',
        padding: '12px',
        fontSize: '16px',
        borderRadius: '8px',
        border: '1px solid #ddd',
        cursor: 'pointer',
    },
    paypalButtons: {
        marginTop: '20px',
    },
    loading: {
        textAlign: 'center',
        padding: '40px',
        color: '#666',
    },
    successMessage: {
        background: '#d4edda',
        color: '#155724',
        padding: '15px',
        borderRadius: '8px',
        marginBottom: '20px',
        textAlign: 'center',
        border: '1px solid #c3e6cb',
    },
    errorMessage: {
        background: '#f8d7da',
        color: '#721c24',
        padding: '15px',
        borderRadius: '8px',
        marginBottom: '20px',
        textAlign: 'center',
        border: '1px solid #f5c6cb',
    },
    backButton: {
        marginTop: '20px',
        width: '100%',
        padding: '12px',
        background: 'transparent',
        border: '2px solid #667eea',
        color: '#667eea',
        borderRadius: '8px',
        cursor: 'pointer',
        fontSize: '16px',
        fontWeight: '600',
        transition: 'all 0.3s ease',
    },
};

export default Checkout;